import { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import Bugsnag from '@bugsnag/js';
import styled from 'styled-components';
import { Box, Button, CTALink, Heading, Interactive, Stack, Text } from '@99designs/emu';
import { mediaQuery } from '@99designs/emu/css';
import { borders, boxShadows, colors, spaces } from '@99designs/emu/settings';
import { useIsExperimentActivated } from '@99designs/experiments';
import { formatMessage } from '@99designs/frontend-components/i18n';
import { IconEdit1, IconRight4 } from '@99designs/icons';
import { useSite } from '@99designs/spa-common';
import { appendUtmParamsToUrl } from '@99designs/tracking';
import { useCreateAssetForProjectMutation, usePrintProductRecommendationsQuery } from '../../graph';
import PulsingBox from '../../lib/components/PulsingBox';
import events from '../../lib/events';
import PrintRecommendationsLogoSelectorModal from '../PrintRecommendationsLogoSelectorModal';

const RecommendedItems = styled(Box)`
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    grid-gap: ${spaces['1.5bu']};

    ${mediaQuery.only('small')} {
        grid-template-columns: repeat(5, minmax(140px, 1fr));
        padding-bottom: ${spaces['0.75bu']};
        overflow: auto;
    }
`;

const ProductImage = styled.img`
    width: 100%;
    aspect-ratio: 10/9;
    object-fit: cover;
    border-radius: ${borders.borderRadiuses.medium}px;
    margin-bottom: ${spaces['0.5bu']};
    transition: box-shadow 0.5s;
`;

const SkeletonImage = styled(PulsingBox)`
    width: 100%;
    aspect-ratio: 10/9;
    border-radius: ${borders.borderRadiuses.medium}px;
    margin-bottom: ${spaces['0.5bu']};
`;

const RecommendedItem = styled(Interactive)`
    :focus {
        border: 1px dashed ${colors.grey700};
        outline: none;
    }

    :hover {
        ${mediaQuery.minimum('medium')} {
            ${ProductImage} {
                box-shadow: ${boxShadows.high};
            }
        }
    }
`;

const MoreOptionsItemLink = styled(Interactive)`
    width: 100%;
    aspect-ratio: 10/9;

    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    border: 3px dashed ${colors.grey400};
    border-radius: ${borders.borderRadiuses.medium}px;
    text-align: center;
    transition: border-color 0.5s;

    ${mediaQuery.only('small')} {
        display: none;
    }

    :hover {
        border-color: ${colors.grey500};
    }
`;

const ArrowBox = styled(Box)`
    display: flex;
    align-items: center;
    justify-content: center;
    height: 56px;
    width: 56px;
    border: 1px solid ${colors.grey800};
    border-radius: 50%;
`;

function MoreOptionsItem({ href }: { href: string }) {
    return (
        <MoreOptionsItemLink href={href}>
            <ArrowBox>
                <IconRight4 width={24} height={24} />
            </ArrowBox>
            <Box maxWidth="122px" marginTop="1bu">
                <Heading accessibilityLevel={5}>{__placeholder('More print options')}</Heading>
            </Box>
        </MoreOptionsItemLink>
    );
}

interface PrintRecommendationsProps {
    logoImages: ({
        id: string;
    } & Parameters<typeof PrintRecommendationsLogoSelectorModal>[0]['options'][0])[];
}

export default function PrintRecommendations({ logoImages }: PrintRecommendationsProps) {
    const [imageUrl, setImageUrl] = useState(logoImages[0].previewUrl);
    const [showLogoSelector, setShowLogoSelector] = useState(false);
    const uploadToDamEnabled = useIsExperimentActivated('labs', 'enabled');

    const intl = useIntl();
    const site = useSite();
    const { data, loading } = usePrintProductRecommendationsQuery({
        variables: {
            locale: site.siteConfig.locale.replace('_', '-'),
            imageUrl,
        },
    });

    const canSwapLogo = logoImages.length > 1;

    const vpProductsHref = `https://www.${site
        .getDomain()
        .replace(
            '99designs',
            'vistaprint'
        )}/?utm_source=99designs&utm_medium=referral&utm_campaign=99d_projectcompleted`;

    const [createAssetForProject] = useCreateAssetForProjectMutation();

    const uploadFileToVista = () => {
        const fileId = logoImages.find((image) => image.previewUrl === imageUrl)?.id;

        if (fileId) {
            createAssetForProject({
                variables: {
                    input: {
                        fileId,
                    },
                },
            }).catch((error) => {
                Bugsnag.notify(error);
            });
        }
    };

    useEffect(() => {
        events.trackRecommendationsViewed('project_completed', 'personalized');
    }, []);

    const handleSwapLogoClicked = () => {
        events.trackSwapLogoButton('project_completed');
        setShowLogoSelector(true);
    };

    return (
        <Box>
            <Box display="flex">
                <Box flexGrow={1} marginBottom="1.5bu">
                    <Box marginBottom="0.5bu">
                        <Heading accessibilityLevel={3}>{__('Your logo, in real life')}</Heading>
                    </Box>
                    <Text>
                        {__(
                            'Swing by VistaPrint to see your logo on business cards, t-shirts and more.'
                        )}
                    </Text>
                </Box>
                {canSwapLogo && (
                    <Box flexGrow={0} flexShrink={0} display={['none', 'block', 'block']}>
                        <Button
                            appearance="tertiary"
                            icon={IconEdit1}
                            onClick={handleSwapLogoClicked}
                        >
                            {__('Swap logo file')}
                        </Button>
                    </Box>
                )}
            </Box>
            <RecommendedItems>
                {loading ? (
                    Array.from({ length: 3 }, (_, i) => (
                        <div key={i} data-testid="recommendations-skeleton">
                            <SkeletonImage />
                            <PulsingBox height="25px" />
                        </div>
                    ))
                ) : (data?.printProductRecommendations.recommendedProducts || []).length > 0 ? (
                    <>
                        {(data?.printProductRecommendations.recommendedProducts || [])
                            .slice(0, 5)
                            .map((item) => (
                                <RecommendedItem
                                    key={item.mpvId}
                                    href={appendUtmParamsToUrl(item.vpProductPageUrl, {
                                        utm_source: '99designs',
                                        utm_medium: 'referral',
                                        utm_campaign: '99d_projectcompleted',
                                    })}
                                    onClick={() => {
                                        uploadToDamEnabled && uploadFileToVista();
                                        events.trackRecommendationsItemClicked(
                                            'project_completed',
                                            item.vpProductPageUrl.split('?')[0],
                                            item.title,
                                            'personalized'
                                        );
                                    }}
                                >
                                    <ProductImage src={item.previewImageUrl} alt="" />
                                    <Heading accessibilityLevel={5}>{item.title}</Heading>
                                    <div>
                                        {formatMessage(__('From {price}'), {
                                            price: intl.formatNumber(item.estimatedPrice.price, {
                                                style: 'currency',
                                                currency: item.estimatedPrice.currency,
                                                minimumFractionDigits: 2,
                                            }),
                                        })}
                                    </div>
                                </RecommendedItem>
                            ))}
                        <MoreOptionsItem href={vpProductsHref} />
                    </>
                ) : (
                    <Box
                        width="100%"
                        marginLeft={['none', 'none', 'none', '3bu']}
                        marginBottom="0.5bu"
                    >
                        <CTALink
                            href={vpProductsHref}
                            onClick={() => {
                                events.trackRecommendationsItemClicked(
                                    'project_completed',
                                    vpProductsHref.split('?')[0],
                                    'home',
                                    'personalized'
                                );
                            }}
                            openInNewTab
                        >
                            {__('See options on VistaPrint')}
                        </CTALink>
                    </Box>
                )}
            </RecommendedItems>
            <Box display={['block', 'none', 'none']} marginTop="1.5bu">
                <Stack space="1bu">
                    <Button href={vpProductsHref} width="fill" openInNewTab>
                        {__('Explore print options')}
                    </Button>
                    {canSwapLogo && (
                        <Button
                            appearance="tertiary"
                            width="fill"
                            icon={IconEdit1}
                            onClick={handleSwapLogoClicked}
                        >
                            {__('Swap logo file')}
                        </Button>
                    )}
                </Stack>
            </Box>

            {showLogoSelector && (
                <PrintRecommendationsLogoSelectorModal
                    options={logoImages}
                    onSelected={(imageUrl) => {
                        if (imageUrl) {
                            events.trackLogoFileSwapped('project_completed');
                            setImageUrl(imageUrl);
                        }
                        setShowLogoSelector(false);
                    }}
                />
            )}
        </Box>
    );
}
